import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { Arrow } from "@/components/tool-kit/diagrams/svg-parts";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { estimateTextWidth } from "@/lib/tool-kit/charts/text";
import { tint } from "@/lib/tool-kit/diagrams/color";
import {
  GANTT_ROW,
  type GanttLayout,
  type GanttRow,
} from "@/lib/tool-kit/diagrams/gantt";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A Gantt chart from `layoutGantt`: a two-row date header, task names on the left, one
 * bar per task with its completed share in full colour, diamonds for milestones, elbow
 * arrows for dependencies and, once the page knows today's date, a dashed "today" line.
 * A dependency that starts before its predecessor ends is drawn dashed in vermillion.
 */

const HEADER_FONT = 11;
const LABEL_FONT = 12;
const WEEKEND = "#f1f1f1";
const STRIPE = "#fafafa";
const VERMILLION = CHART_COLORS[3];
const PROGRESS_FONT = 10;

function Header({ layout }: { layout: GanttLayout }) {
  const upperY = layout.headerTop;
  const lowerY = layout.headerTop + 18;
  return (
    <g>
      {layout.upper.map((cell) => (
        <g key={`upper-${cell.x}`}>
          <line
            stroke={CHART_INK.grid}
            x1={px(cell.x)}
            x2={px(cell.x)}
            y1={px(upperY)}
            y2={px(lowerY)}
          />
          <text
            fill={CHART_INK.text}
            fontSize={HEADER_FONT}
            fontWeight="bold"
            x={px(cell.x + 4)}
            y={px(upperY + 13)}
          >
            {cell.label}
          </text>
        </g>
      ))}
      {layout.lower.map((cell) => (
        <text
          fill={CHART_INK.muted}
          fontSize={HEADER_FONT}
          key={`lower-${cell.x}`}
          textAnchor="middle"
          x={px(cell.x + cell.width / 2)}
          y={px(lowerY + 13)}
        >
          {cell.label}
        </text>
      ))}
      <line
        stroke={CHART_INK.axis}
        x1={px(layout.originX)}
        x2={px(layout.width - 16)}
        y1={px(layout.top - 2)}
        y2={px(layout.top - 2)}
      />
    </g>
  );
}

function Bar({ row, color }: { row: GanttRow; color: string }) {
  const bar = row.bar;
  if (!bar) {
    return null;
  }
  const middle = row.y + GANTT_ROW / 2;
  if (row.kind === "group") {
    const right = bar.x + bar.width;
    return (
      <g>
        <rect
          fill={CHART_INK.text}
          height={px(bar.height)}
          width={px(bar.width)}
          x={px(bar.x)}
          y={px(bar.y)}
        />
        <polygon
          fill={CHART_INK.text}
          points={`${px(bar.x)},${px(bar.y + bar.height)} ${px(bar.x + 6)},${px(bar.y + bar.height)} ${px(bar.x)},${px(bar.y + bar.height + 5)}`}
        />
        <polygon
          fill={CHART_INK.text}
          points={`${px(right)},${px(bar.y + bar.height)} ${px(right - 6)},${px(bar.y + bar.height)} ${px(right)},${px(bar.y + bar.height + 5)}`}
        />
      </g>
    );
  }
  if (row.milestone) {
    const half = 7;
    return (
      <polygon
        fill={CHART_INK.text}
        points={`${px(bar.x)},${px(middle - half)} ${px(bar.x + half)},${px(middle)} ${px(bar.x)},${px(middle + half)} ${px(bar.x - half)},${px(middle)}`}
        stroke={CHART_INK.background}
        strokeWidth={1}
      />
    );
  }
  const done = row.progress === null ? 0 : (bar.width * row.progress) / 100;
  return (
    <g>
      <rect
        fill={tint(color, row.progress === null ? 0.15 : 0.6)}
        height={px(bar.height)}
        rx={3}
        stroke={color}
        strokeWidth={1}
        width={px(bar.width)}
        x={px(bar.x)}
        y={px(bar.y)}
      />
      {done > 0 ? (
        <rect
          fill={color}
          height={px(bar.height)}
          rx={3}
          width={px(done)}
          x={px(bar.x)}
          y={px(bar.y)}
        />
      ) : null}
    </g>
  );
}

/**
 * A task's progress written on its bar, at the right end — or, on a bar too short to
 * hold it, beyond the bar and past the elbow of any dependency arrow leaving it.
 */
function ProgressText({
  row,
  color,
  text,
}: {
  row: GanttRow;
  color: string;
  text: string;
}) {
  const bar = row.bar;
  if (!bar || row.milestone || row.progress === null) {
    return null;
  }
  const width = estimateTextWidth(text, { fontSize: PROGRESS_FONT });
  const y = row.y + GANTT_ROW / 2 + PROGRESS_FONT * 0.35;
  if (bar.width >= width + 10) {
    const onDone = (bar.width * row.progress) / 100 >= bar.width - 4;
    return (
      <text
        fill={onDone ? textColorOn(color) : CHART_INK.text}
        fontSize={PROGRESS_FONT}
        textAnchor="end"
        x={px(bar.x + bar.width - 5)}
        y={px(y)}
      >
        {text}
      </text>
    );
  }
  return (
    <text
      fill={CHART_INK.muted}
      fontSize={PROGRESS_FONT}
      x={px(bar.x + bar.width + 12)}
      y={px(y)}
    >
      {text}
    </text>
  );
}

export function GanttDiagram({
  id,
  layout,
  colors,
  today,
  todayLabel,
  progressLabel,
  label,
  description,
}: {
  id: string;
  layout: GanttLayout;
  /** One colour per group; index −1 (tasks without a group) uses the first. */
  colors: readonly string[];
  /** Today's day number, known only after mount; null on the server. */
  today: number | null;
  todayLabel: string;
  /** "60%" for a progress value. */
  progressLabel: (progress: number) => string;
  label: string;
  description: string;
}) {
  const colorOf = (group: number) => colors[Math.max(0, group)] ?? colors[0];
  const todayX =
    today !== null && today >= layout.firstDay && today < layout.lastDay
      ? layout.originX + (today - layout.firstDay + 0.5) * layout.dayWidth
      : null;
  return (
    <ChartSvg
      className="block h-auto min-w-full max-w-none"
      description={description}
      height={layout.height}
      id={id}
      label={label}
      width={layout.width}
    >
      <ChartTitle title={layout.title} />
      {layout.rows.map((row, index) =>
        index % 2 === 1 ? (
          <rect
            fill={STRIPE}
            height={GANTT_ROW}
            key={`stripe-${row.y}`}
            width={px(layout.width - 32)}
            x={16}
            y={px(row.y)}
          />
        ) : null
      )}
      {layout.weekendShade.map((shade) => (
        <rect
          fill={WEEKEND}
          height={px(shade.height)}
          key={`weekend-${shade.x}`}
          width={px(shade.width)}
          x={px(shade.x)}
          y={px(shade.y)}
        />
      ))}
      {layout.grid.map((x) => (
        <line
          key={`grid-${x}`}
          stroke={CHART_INK.grid}
          strokeWidth={0.75}
          x1={px(x)}
          x2={px(x)}
          y1={px(layout.top - 2)}
          y2={px(layout.bottom)}
        />
      ))}
      <Header layout={layout} />
      {layout.rows.map((row) => (
        <g key={`row-${row.y}`}>
          <text
            fill={CHART_INK.text}
            fontSize={LABEL_FONT}
            fontWeight={row.kind === "group" ? "bold" : undefined}
            x={px(16 + (row.kind === "task" && row.group >= 0 ? 12 : 0))}
            y={px(row.y + GANTT_ROW / 2 + LABEL_FONT * 0.35)}
          >
            {row.label}
          </text>
          <Bar color={colorOf(row.group)} row={row} />
          {row.kind === "task" && row.progress !== null ? (
            <ProgressText
              color={colorOf(row.group)}
              row={row}
              text={progressLabel(row.progress)}
            />
          ) : null}
        </g>
      ))}
      {layout.arrows.map((arrow) => (
        <Arrow
          color={arrow.early ? VERMILLION : CHART_INK.muted}
          dashed={arrow.early}
          head={6}
          key={`arrow-${arrow.from}-${arrow.to}`}
          points={arrow.points}
          width={1.1}
        />
      ))}
      {todayX === null ? null : (
        <g>
          <line
            stroke={VERMILLION}
            strokeDasharray="4 3"
            strokeWidth={1.5}
            x1={px(todayX)}
            x2={px(todayX)}
            y1={px(layout.top - 2)}
            y2={px(layout.bottom)}
          />
          <text
            fill={VERMILLION}
            fontSize={10}
            fontWeight="bold"
            textAnchor="middle"
            x={px(todayX)}
            y={px(layout.bottom + 11)}
          >
            {todayLabel}
          </text>
        </g>
      )}
    </ChartSvg>
  );
}
